/**
 * Inbound Google Drive/Sheets sync wire shapes (docs/google-drive-sync-plan.md).
 *
 * The Sheet + Drive folder are PER LANGUAGE: each (team, languoid) a team works
 * on has its own source. A team's config is therefore team-level policy
 * (auto-advance / write-back / cadence) plus one `LanguoidSyncSource` per language.
 *
 * Per-team, gated on `can_configure_sheets_sync`. Auth is one project-level Google
 * service account (no per-team OAuth); teams share each language's sheet + folder
 * with `serviceAccountEmail`. No credential reaches the desktop.
 */

import { z } from 'zod';
import type { RowErrorDetail } from '../import.js';

/**
 * Result of one language's inbound run. `created/updated/skipped/locked` mirror
 * the importer's metadata classification; `audio*` cover the Drive audio ingest +
 * state driving; `statusRowsWritten` covers the write-back.
 */
export interface DriveSyncRunResult {
  languoidCode: string;
  ranAt: string;
  ok: boolean;
  error: string | null;
  segments: number;
  created: number;
  updated: number;
  skipped: number;
  locked: number;
  rowErrors: number;
  /** Per-segment reasons behind `rowErrors` (capped for size); `rowErrors` is the true total. */
  rowErrorDetails: RowErrorDetail[];
  /** DRAFT challenges published to READY_TO_RECORD this run (auto-publish on). */
  challengesPublished: number;
  audioIngested: number;
  challengesAdvanced: number;
  audioErrors: number;
  statusRowsWritten: number;
  /** Round-trip (driveSyncRoundTrip): AI_Translation/AI_Reasoning cells written to the input tab. */
  aiResultsWritten: number;
  /** Round-trip: sheet verdicts read back and applied as single-rater judgements. */
  judgementsApplied: number;
  /** Round-trip: challenges driven all the way to COMPLETED (APPROVED) from the sheet this run. */
  challengesCompleted: number;
}

/** One language's sync source + its last-run status (server → desktop). */
export interface LanguoidSyncSource {
  languoidCode: string;
  /** Resolved English name, for display. */
  languageName: string;
  enabled: boolean;
  spreadsheetId: string | null;
  /** Tab to read metadata rows from; null = the spreadsheet's first tab. */
  sheetTab: string | null;
  driveFolderId: string | null;
  /** This language's QA write-back sheet for mobile QC results; null when unset. */
  reviewSheetId: string | null;
  /** Reverse challenges (docs/reverse-challenges-plan.md): the English-authored sheet the team
   *  translates + the per-team folder for the native-language QUESTION audio. The shared English
   *  SEGMENT audio folder is project-level (ProjectSystemInfo.reverseSegmentFolderId), not here. */
  reverseEnabled: boolean;
  reverseSpreadsheetId: string | null;
  reverseSheetTab: string | null;
  reverseQuestionFolderId: string | null;
  lastSyncAt: string | null;
  lastError: string | null;
  lastResult: DriveSyncRunResult | null;
}

/** A team's full sync config: team-level policy + one source per language. */
export interface DriveSyncConfig {
  /** Drive ingested challenges toward READY_TO_TEST without manual stage gates. */
  autoAdvance: boolean;
  /** Publish imported DRAFT challenges to READY_TO_RECORD (so recorders see them). */
  autoPublish: boolean;
  /** Write a recorder-facing status tab back to each language's sheet. */
  writeBack: boolean;
  /** Round-trip test results + ratings through the language's input tab (writes to it). */
  roundTrip: boolean;
  /** This team's cadence override in minutes; null = inherit the project default. */
  cadenceMinutes: number | null;
  /** The project-wide default cadence (minutes) — what `null` cadence inherits. */
  defaultCadenceMinutes: number;
  /** The service-account email to share each sheet + folder with (read-only). */
  serviceAccountEmail: string | null;
  /** True when the server has a service-account credential configured at all. */
  configured: boolean;
  /** One per language the team handles (Team.languoidCodes). */
  sources: LanguoidSyncSource[];
}

// --- Project-level sync (observer dashboard "Sync now" + log) ----------------

/** One language source's sync state, flattened across teams for the project view. */
export interface ProjectSyncSource {
  teamId: string;
  teamName: string;
  languoidCode: string;
  languageName: string;
  enabled: boolean;
  hasSpreadsheet: boolean;
  lastSyncAt: string | null;
  lastError: string | null;
  lastResult: DriveSyncRunResult | null;
  /** Next scheduled auto-sync (lastSyncAt + the team's cadence), clamped to now; null if not runnable. */
  nextSyncAt: string | null;
}

/**
 * Project-wide sync status for the observer dashboard. Returned by both `GET /project/sync`
 * (just the status + log) and `POST /project/sync` (after running every team's enabled sources).
 */
export interface ProjectSyncStatus {
  /** A Google service account is configured on the server at all. */
  configured: boolean;
  /** The background scheduler is running (REDIS_URL set) — else auto-sync is off. */
  schedulerEnabled: boolean;
  /** Soonest upcoming auto-sync across enabled sources; null when none / scheduler off. */
  nextAutoSyncAt: string | null;
  /** Most recent sync across all sources. */
  lastSyncAt: string | null;
  /** One row per (team, language) source, newest activity first. */
  sources: ProjectSyncSource[];
  /** When this status was computed (server clock). */
  ranAt: string;
}

/** PATCH body for the team-level policy. */
export const driveSyncPolicyUpdateSchema = z.object({
  autoAdvance: z.boolean().optional(),
  autoPublish: z.boolean().optional(),
  writeBack: z.boolean().optional(),
  roundTrip: z.boolean().optional(),
  /** Team cadence override (minutes); `null` clears it to inherit the project default. */
  cadenceMinutes: z.number().int().min(5).max(1440).nullable().optional(),
});
export type DriveSyncPolicyUpdate = z.infer<typeof driveSyncPolicyUpdateSchema>;

/** PUT body for one language's source (every field optional, patch-style). */
export const languoidSyncUpdateSchema = z.object({
  enabled: z.boolean().optional(),
  spreadsheetId: z.string().trim().max(200).nullish(),
  sheetTab: z.string().trim().max(200).nullish(),
  driveFolderId: z.string().trim().max(200).nullish(),
  reviewSheetId: z.string().trim().max(200).nullish(),
  // Reverse-challenge source (docs/reverse-challenges-plan.md).
  reverseEnabled: z.boolean().optional(),
  reverseSpreadsheetId: z.string().trim().max(200).nullish(),
  reverseSheetTab: z.string().trim().max(200).nullish(),
  reverseQuestionFolderId: z.string().trim().max(200).nullish(),
});
export type LanguoidSyncUpdate = z.infer<typeof languoidSyncUpdateSchema>;
